import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'

import {dispatchKeys} from '../dispatch/query-keys'
import {jobKeys} from '../jobs/query-keys'
import {invalidateEquipmentMoves} from '../project-deployed-equipment/invalidate-equipment-moves'
import {workKeys} from '../work/query-keys'
import {
    approveProject,
    archiveProject,
    archiveProjectBillableLineItem,
    createProject,
    createProjectBillableLineItem,
    createProjectBillingTerms,
    createProjectJob,
    createProjectJobsBatch,
    getProject,
    listProjectBillableLineItems,
    listProjectBillingTerms,
    listProjects,
    patchProject,
    reopenProject,
    updateProjectBillableLineItem,
} from './api'
import {projectKeys} from './query-keys'
import type {
    CreateProjectBillableLineItemInput,
    CreateProjectBillingTermsInput,
    CreateProjectInput,
    CreateProjectJobInput,
    PatchProjectInput,
    ProjectListFilters,
} from './schemas'

export function useProjects(filters: ProjectListFilters = {}, options?: {enabled?: boolean}) {
    return useQuery({
        queryKey: projectKeys.list(filters),
        queryFn: () => listProjects(filters),
        enabled: options?.enabled ?? true,
    })
}

export function useProject(projectId: string, options?: {enabled?: boolean}) {
    return useQuery({
        queryKey: projectKeys.detail(projectId),
        queryFn: () => getProject(projectId),
        enabled: options?.enabled ?? Boolean(projectId),
    })
}

export function useProjectBillableLineItems(projectId: string, options?: {enabled?: boolean}) {
    return useQuery({
        queryKey: projectKeys.billableLineItems(projectId),
        queryFn: () => listProjectBillableLineItems(projectId),
        enabled: options?.enabled ?? Boolean(projectId),
    })
}

export function useProjectBillingTerms(projectId: string, options?: {enabled?: boolean}) {
    return useQuery({
        queryKey: projectKeys.billingTerms(projectId),
        queryFn: () => listProjectBillingTerms(projectId),
        enabled: options?.enabled ?? Boolean(projectId),
    })
}

export function useCreateProject() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (input: CreateProjectInput) => createProject(input),
        onSuccess: () => {
            void queryClient.invalidateQueries({queryKey: projectKeys.all})
        },
    })
}

export function usePatchProject(projectId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (input: PatchProjectInput) => patchProject(projectId, input),
        onSuccess: () => {
            void queryClient.invalidateQueries({queryKey: projectKeys.detail(projectId)})
            void queryClient.invalidateQueries({queryKey: projectKeys.lists()})
        },
    })
}

export function useApproveProject(projectId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: () => approveProject(projectId),
        onSuccess: () => {
            void queryClient.invalidateQueries({queryKey: projectKeys.detail(projectId)})
            void queryClient.invalidateQueries({queryKey: projectKeys.lists()})
        },
    })
}

export function useArchiveProject(projectId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (options: {confirmCancelPlanned?: boolean} = {}) =>
            archiveProject(projectId, options),
        onSuccess: () => {
            void invalidateEquipmentMoves(queryClient, projectId)
            void queryClient.invalidateQueries({queryKey: dispatchKeys.all})
        },
    })
}

export function useReopenProject(projectId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: () => reopenProject(projectId),
        onSuccess: () => {
            void queryClient.invalidateQueries({queryKey: projectKeys.detail(projectId)})
            void queryClient.invalidateQueries({queryKey: projectKeys.lists()})
        },
    })
}

export function useRecentProjectsByCustomer(customerId: string | null) {
    return useProjects(
        {
            customerId: customerId ?? undefined,
            sortBy: 'created_at',
            sortDir: 'desc',
        },
        {enabled: Boolean(customerId)},
    )
}

export function useCreateProjectBillingTerms(projectId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (input: CreateProjectBillingTermsInput) =>
            createProjectBillingTerms(projectId, input),
        onSuccess: () => {
            void queryClient.invalidateQueries({queryKey: projectKeys.detail(projectId)})
            void queryClient.invalidateQueries({queryKey: projectKeys.billingTerms(projectId)})
        },
    })
}

export function useCreateProjectJob(projectId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (input: CreateProjectJobInput) => createProjectJob(projectId, input),
        onSuccess: () => {
            void queryClient.invalidateQueries({queryKey: projectKeys.detail(projectId)})
            void queryClient.invalidateQueries({queryKey: jobKeys.lists()})
        },
    })
}

export function useCreateProjectJobsBatch(projectId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: ({inputs, requestId}: {inputs: CreateProjectJobInput[]; requestId: string}) =>
            createProjectJobsBatch(projectId, inputs, requestId),
        onSuccess: () => {
            void queryClient.invalidateQueries({queryKey: projectKeys.detail(projectId)})
            void queryClient.invalidateQueries({queryKey: projectKeys.lists()})
            void queryClient.invalidateQueries({queryKey: jobKeys.all})
            void queryClient.invalidateQueries({queryKey: workKeys.all})
        },
    })
}

export function useCreateProjectBillableLineItem(projectId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (input: CreateProjectBillableLineItemInput) =>
            createProjectBillableLineItem(projectId, input),
        onSuccess: () => {
            void queryClient.invalidateQueries({queryKey: projectKeys.billableLineItems(projectId)})
            void queryClient.invalidateQueries({queryKey: projectKeys.detail(projectId)})
        },
    })
}

export function useUpdateProjectBillableLineItem(projectId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: ({
            lineItemId,
            input,
        }: {
            lineItemId: string
            input: CreateProjectBillableLineItemInput
        }) => updateProjectBillableLineItem(projectId, lineItemId, input),
        onSuccess: () => {
            void queryClient.invalidateQueries({queryKey: projectKeys.billableLineItems(projectId)})
            void queryClient.invalidateQueries({queryKey: projectKeys.detail(projectId)})
        },
    })
}

export function useArchiveProjectBillableLineItem(projectId: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (lineItemId: string) => archiveProjectBillableLineItem(projectId, lineItemId),
        onSuccess: () => {
            void queryClient.invalidateQueries({queryKey: projectKeys.billableLineItems(projectId)})
            void queryClient.invalidateQueries({queryKey: projectKeys.detail(projectId)})
        },
    })
}
